import {
  useProposalApplication,
  useProposalApplicationSearch,
} from "./use-proposal-application";
import {
  isApplicationBuilderProposal,
  applicationProposalFieldContext,
  resolveProposalFieldContext,
} from "@lib/applicationProposalDisplay";
import { useRound, useStateSelector } from "@client/pages/round/round-context";
import { useEffect, useState, useMemo } from "react";

export function useAllFieldResponses(): Record<string, Record<string, string>> {
  const infoStore = useStateSelector((state) => state.infoStore);
  const [fieldResponses, setFieldResponses] = useState<
    Record<string, Record<string, string>>
  >(() => infoStore.getAllFieldResponses());

  useEffect(() => {
    const unsubscribe = infoStore.subscribeToAllFieldResponses(
      (fieldResponses) => {
        setFieldResponses(fieldResponses);
      },
    );

    return unsubscribe;
  }, [infoStore]);

  const round = useRound();
  const hasBuilderProposals = Object.values(round.proposalForms).some(
    (form) =>
      form.status === "active" &&
      Object.values(form.proposals).some((proposal) =>
        isApplicationBuilderProposal(proposal, form.id),
      ),
  );
  const application = useProposalApplicationSearch(
    round.id,
    hasBuilderProposals,
  );
  return useMemo(
    () =>
      application.data
        ? {
            ...fieldResponses,
            ...Object.fromEntries(
              Object.entries(application.data).map(([id, answers]) => [
                id,
                answers ?? {},
              ]),
            ),
          }
        : fieldResponses,
    [fieldResponses, application.data],
  );
}

export function useProposalFieldContext(proposalId: string) {
  const infoStore = useStateSelector((state) => state.infoStore);
  const [fieldResponses, setFieldResponses] = useState<Record<string, string>>(
    () => infoStore.readNewestFieldResponses(proposalId) ?? {},
  );

  useEffect(() => {
    if (!proposalId) {
      return;
    }

    const unsubscribe = infoStore.subscribeToFieldResponses(
      proposalId,
      (fieldResponses) => {
        setFieldResponses(fieldResponses);
      },
    );

    return unsubscribe;
  }, [proposalId, infoStore]);

  const round = useRound();
  const form = Object.values(round.proposalForms).find(
    (form) => form.proposals[proposalId],
  );
  const proposal = form?.proposals[proposalId];
  const isBuilder =
    !!form && !!proposal && isApplicationBuilderProposal(proposal, form.id);
  const application = useProposalApplication(
    { roundId: round.id, proposalId },
    isBuilder,
  );
  return useMemo(() => {
    const content = resolveProposalFieldContext(
      proposal,
      form,
      fieldResponses,
      application.data
        ? applicationProposalFieldContext(application.data.sections)
        : null,
    );

    if (!isBuilder) {
      return {
        ...content!,
        isBuilder,
        sourceStatus: "ready" as const,
        sourceMessage: null,
      };
    }

    return {
      ...(content ?? { fields: [], fieldResponses: {} }),
      isBuilder,
      sourceStatus: application.error
        ? ("error" as const)
        : application.data
          ? ("ready" as const)
          : ("pending" as const),
      sourceMessage: application.error
        ? `Unable to load the submitted application. ${application.error.message}`
        : application.data
          ? null
          : "Loading submitted application…",
    };
  }, [
    application.data,
    application.error,
    fieldResponses,
    form,
    proposal,
    isBuilder,
  ]);
}

export function useFieldResponsesForProposal(
  proposalId: string,
): Record<string, string> {
  return useProposalFieldContext(proposalId).fieldResponses;
}
